import { useState, useEffect, useEffectEvent } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FileText, DollarSign, Search, RefreshCw, Download, Calendar } from 'lucide-react'
import { MonthPicker } from '@/components/ui/month-picker'
import { ClienteCombobox } from '@/components/ui/cliente-combobox'
import { MovimientosTable } from '@/components/movimientos/MovimientosTable'
import { MovimientoFormDialog } from '@/components/movimientos/MovimientoFormDialog'
import { RegistrarPagoDialog } from '@/components/movimientos/registrar-pago'
import { GenerarLoteDialog } from '@/components/movimientos/GenerarLoteDialog'
import type { MovimientoFormValues } from '@/components/movimientos/MovimientoForm'
import {
  useMovimientos,
  useCuentas,
  useCrearMovimiento,
  useActualizarMovimiento,
  useEliminarMovimiento,
} from '@/hooks/useMovimientos'
import { useClientes } from '@/hooks/useClientes'
import { useServicios } from '@/hooks/useServicios'
import { useNotificaciones } from '@/hooks/useNotificaciones'
import { movimientosService } from '@/services/movimientosService'
import { ComprobanteViewer } from '@/components/movimientos/ComprobanteViewer'
import { usePermissions } from '@/hooks/usePermissions'
import type { Movimiento, MovimientoConCliente } from '@/types/database'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { handleMutationError } from '@/lib/permisos-guard'
import { mensajeDeError } from '@/lib/errores-postgres'

export function Movimientos() {
  const { user } = useAuth0()
  const { puede } = usePermissions()
  const [searchParams, setSearchParams] = useSearchParams()

  // Estado para filtros
  const [busqueda, setBusqueda] = useState('')
  const [filtroTipo, setFiltroTipo] = useState<string>('todos')
  const [filtroCuenta, setFiltroCuenta] = useState<string>('todos')
  const [filtroPeriodo, setFiltroPeriodo] = useState<string | null>(null)
  const [filtroClienteId, setFiltroClienteId] = useState<string>('')

  // Estado para dialogs
  const [dialogOpen, setDialogOpen] = useState(false)
  const [tipoFormulario, setTipoFormulario] = useState<'Deuda' | 'Pago' | null>(null)
  const [generarLoteOpen, setGenerarLoteOpen] = useState(false)
  const [registrarPagoOpen, setRegistrarPagoOpen] = useState(false)
  const [movimientoEditar, setMovimientoEditar] = useState<Movimiento | null>(null)
  const [movimientoEliminar, setMovimientoEliminar] = useState<MovimientoConCliente | null>(null)
  const [comprobanteViewerId, setComprobanteViewerId] = useState<string | null>(null)

  // Queries y mutations
  const {
    data: movimientos = [],
    isLoading,
    error,
    refetch,
  } = useMovimientos({
    tipo: filtroTipo !== 'todos' ? (filtroTipo as 'Deuda' | 'Pago') : undefined,
    cuentaId: filtroCuenta !== 'todos' ? filtroCuenta : undefined,
    mesAnio: filtroPeriodo ?? undefined,
    clienteId: filtroClienteId && filtroClienteId !== 'todos' ? filtroClienteId : undefined,
  })

  const { data: clientes = [] } = useClientes()
  const { data: cuentas = [] } = useCuentas()
  const { data: servicios = [] } = useServicios()

  const crearMovimiento = useCrearMovimiento()
  const actualizarMovimiento = useActualizarMovimiento()
  const eliminarMovimiento = useEliminarMovimiento()

  // Auto-abrir dialog de generar lote si viene desde dashboard
  const openGenerarLoteFromSearchParam = useEffectEvent(() => {
    if (searchParams.get('action') === 'generar-lote') {
      if (puede.generarLoteMovimientos && puede.verHonorariosCliente) setGenerarLoteOpen(true)
      searchParams.delete('action')
      setSearchParams(searchParams)
    }
  })
  useEffect(() => {
    openGenerarLoteFromSearchParam()
  }, [searchParams])
  const { notificarNuevoMovimiento } = useNotificaciones()

  // Filtrar por búsqueda local (concepto o nombre de cliente)
  const movimientosFiltrados = movimientos.filter((mov) => {
    if (!busqueda) return true
    const termino = busqueda.toLowerCase()
    const nombreCliente = mov.cliente?.persona?.nombre_completo ?? ''
    return (
      mov.concepto.toLowerCase().includes(termino) ||
      nombreCliente.toLowerCase().includes(termino)
    )
  })

  const [mostrarSaldo, setMostrarSaldo] = useState(true)
  const clientesUnicos = new Set(
    movimientosFiltrados.map(
      (mov) =>
        mov.cliente_id ||
        mov.cliente?.id ||
        mov.cliente?.persona?.nombre_completo ||
        'sin-cliente'
    )
  )
  const puedeMostrarSaldo = clientesUnicos.size <= 1

  // Handlers
  const handleNuevaDeuda = () => {
    setMovimientoEditar(null)
    setTipoFormulario('Deuda')
    setDialogOpen(true)
  }

  const handleNuevoPago = () => {
    setRegistrarPagoOpen(true)
  }

  const handleEditarMovimiento = (movimiento: MovimientoConCliente) => {
    setMovimientoEditar(movimiento as Movimiento)
    setTipoFormulario(null)
    setDialogOpen(true)
  }

  const handleEliminarMovimiento = (movimiento: MovimientoConCliente) => {
    setMovimientoEliminar(movimiento)
  }

  const handleVerComprobante = (movimiento: MovimientoConCliente) => {
    const docId = movimiento._adjuntoDocumentoId
    if (!docId) return
    setComprobanteViewerId(docId)
  }

  const handleSubmit = async (data: MovimientoFormValues) => {
    try {
      const movimientoData = {
        cliente_id: data.cliente_id,
        concepto: data.concepto,
        fecha: data.fecha,
        debe: data.debe,
        haber: data.haber,
        monto: data.debe || data.haber || 0,
        tipo: data.tipo,
        tipo_deuda: data.tipo_deuda,
        servicio_id: data.servicio_id,
        cuenta_id: data.cuenta_id,
        mes_anio: data.mes_anio,
        observaciones: data.observaciones,
        created_by: user?.email ?? 'sistema',
      }

      if (movimientoEditar) {
        await actualizarMovimiento.mutateAsync({
          id: movimientoEditar.id,
          data: movimientoData,
        })
        toast.success('Movimiento actualizado correctamente')
      } else {
        await crearMovimiento.mutateAsync(movimientoData)
        toast.success('Movimiento creado correctamente')
        const cliente = clientes.find(c => c.id === data.cliente_id)
        if (cliente) {
          notificarNuevoMovimiento({
            clienteNombre: cliente.nombre ?? '',
            concepto: data.concepto,
            monto: data.debe || data.haber || 0,
            tipo: data.debe && data.debe > 0 ? 'debe' : 'haber',
          })
        }
      }
      setDialogOpen(false)
      setMovimientoEditar(null)
    } catch (err) {
      const fallback = movimientoEditar
        ? 'Error al actualizar el movimiento'
        : 'Error al crear el movimiento'
      handleMutationError(err, mensajeDeError(err, fallback))
    }
  }

  const handleConfirmarEliminar = async () => {
    if (!movimientoEliminar) return
    try {
      await eliminarMovimiento.mutateAsync(movimientoEliminar.id)
      toast.success('Movimiento eliminado correctamente')
      setMovimientoEliminar(null)
    } catch (err) {
      handleMutationError(err, 'Error al eliminar el movimiento')
    }
  }

  const handleExportar = () => {
    const cuentasMap = new Map(cuentas.map(c => [c.id, c.nombre]))
    const serviciosMap = new Map(servicios.map(s => [s.id, s.nombre]))
    movimientosService.descargarCSV(movimientosFiltrados, { cuentasMap, serviciosMap })
    toast.success('Archivo exportado correctamente')
  }

  const limpiarFiltros = () => {
    setBusqueda('')
    setFiltroTipo('todos')
    setFiltroCuenta('todos')
    setFiltroPeriodo(null)
    setFiltroClienteId('')
  }

  const handleRefresh = () => {
    limpiarFiltros()
    refetch()
  }

  const hayFiltrosActivos =
    filtroTipo !== 'todos' ||
    filtroCuenta !== 'todos' ||
    filtroPeriodo !== null ||
    (filtroClienteId && filtroClienteId !== 'todos')

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Movimientos"
        description="Registro de pagos y deudas"
      />

      <div className="flex-1 flex flex-col p-4 md:p-6 gap-4 md:gap-6 min-h-0">
        {/* Fila 1: Filtros */}
        <div className="shrink-0 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por concepto o cliente..."
              value={busqueda}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBusqueda(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="w-full sm:w-[200px]">
            <MonthPicker
              value={filtroPeriodo}
              onValueChange={setFiltroPeriodo}
              placeholder="Todos los períodos"
            />
          </div>

          <div className="w-full sm:w-[220px]">
            <ClienteCombobox
              clientes={clientes}
              value={filtroClienteId}
              onValueChange={setFiltroClienteId}
              placeholder="Filtrar por cliente..."
              showAllOption={{ value: 'todos', label: 'Todos los clientes' }}
            />
          </div>

          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue>
                {filtroTipo === 'todos' ? 'Tipo: Todos' : filtroTipo === 'Deuda' ? 'Deudas' : 'Pagos'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Tipo: Todos</SelectItem>
              <SelectItem value="Deuda">Deudas</SelectItem>
              <SelectItem value="Pago">Pagos</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filtroCuenta} onValueChange={setFiltroCuenta}>
            <SelectTrigger className="hidden sm:flex w-[160px]">
              <SelectValue>
                {filtroCuenta === 'todos' ? 'Cuenta: Todas' : cuentas.find(c => c.id === filtroCuenta)?.nombre || 'Cuenta'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Cuenta: Todas</SelectItem>
              {cuentas.map((cuenta) => (
                <SelectItem key={cuenta.id} value={cuenta.id}>
                  {cuenta.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hayFiltrosActivos && (
            <Button variant="ghost" size="sm" onClick={limpiarFiltros}>
              Limpiar filtros
            </Button>
          )}
        </div>

        {/* Fila 2: Acciones */}
        <div className="shrink-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="mostrar-saldo"
              checked={mostrarSaldo && puedeMostrarSaldo}
              onCheckedChange={setMostrarSaldo}
              disabled={!puedeMostrarSaldo}
            />
            <label
              htmlFor="mostrar-saldo"
              className={`text-sm cursor-pointer ${!puedeMostrarSaldo ? 'text-muted-foreground' : ''}`}
            >
              Saldo acumulado
            </label>
            {!puedeMostrarSaldo && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Disponible al filtrar un cliente
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="icon" onClick={handleRefresh} title="Actualizar y limpiar filtros">
              <RefreshCw className="h-4 w-4" />
            </Button>
            {puede.exportarReportes && (
              <Button variant="outline" className="hidden md:flex" onClick={handleExportar}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            )}
            {puede.generarLoteMovimientos && puede.verHonorariosCliente && (
              <Button variant="secondary" className="hidden sm:flex" onClick={() => setGenerarLoteOpen(true)}>
                <Calendar className="h-4 w-4 mr-2" />
                <span className="hidden md:inline">Generar</span> Lote
              </Button>
            )}
            {puede.crearMovimiento && (
              <Button variant="outline" size="sm" onClick={handleNuevaDeuda}>
                <FileText className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Registrar</span> Deuda
              </Button>
            )}
            {puede.crearMovimiento && (
              <Button size="sm" onClick={handleNuevoPago}>
                <DollarSign className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Registrar</span> Pago
              </Button>
            )}
          </div>
        </div>

        {/* Tabla de movimientos - contenedor con scroll interno */}
        <div className="flex-1 min-h-0">
          {error ? (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <p className="text-destructive">
                Error al cargar los movimientos. Verifica la conexión con Supabase.
              </p>
            </div>
          ) : (
            <MovimientosTable
              movimientos={movimientosFiltrados}
              isLoading={isLoading}
              onEditar={handleEditarMovimiento}
              onEliminar={handleEliminarMovimiento}
              onVerComprobante={handleVerComprobante}
              showCliente={true}
              showSaldo={mostrarSaldo && puedeMostrarSaldo}
              canEdit={puede.editarMovimiento}
              canDelete={puede.eliminarMovimiento}
            />
          )}
        </div>
      </div>

      {/* Dialog para crear/editar movimiento */}
      <MovimientoFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        movimiento={movimientoEditar}
        clientes={clientes}
        cuentas={cuentas}
        onSubmit={handleSubmit}
        isLoading={crearMovimiento.isPending || actualizarMovimiento.isPending}
        tipoFijo={!movimientoEditar ? tipoFormulario || undefined : undefined}
      />

      {/* Dialog de confirmacion para eliminar */}
      <AlertDialog
        open={!!movimientoEliminar}
        onOpenChange={(open: boolean) => !open && setMovimientoEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar movimiento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara permanentemente el
              movimiento <strong>{movimientoEliminar?.concepto}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarEliminar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminarMovimiento.isPending ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para generar movimientos en lote */}
      <GenerarLoteDialog
        open={generarLoteOpen}
        onOpenChange={setGenerarLoteOpen}
      />

      {/* Dialog para registrar pago con selección de deudas */}
      <RegistrarPagoDialog
        open={registrarPagoOpen}
        onOpenChange={setRegistrarPagoOpen}
        clientes={clientes}
        cuentas={cuentas}
      />

      {/* Visor de comprobante */}
      <ComprobanteViewer
        open={!!comprobanteViewerId}
        onOpenChange={(open) => !open && setComprobanteViewerId(null)}
        documentoId={comprobanteViewerId}
      />
    </div>
  )
}
